import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { CurrentService } from './current.service';
import { CustomerService } from './customer.service';
import { inject } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ToastService } from './toast.service';
import { logError } from './helpers/sentry.helper';
import { ModalService } from 'src/app/services/modal.service';

export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    options?: ErrorOptions
  ) {
    super(message, options);
  }
}

export abstract class BaseService {
  private readonly http = inject(HttpClient);
  private readonly toastService = inject(ToastService);
  protected readonly current = inject(CurrentService);
  protected readonly customer = inject(CustomerService);
  protected readonly modalService = inject(ModalService);

  private async callApi<T>(request: Observable<T>, handleError: boolean = true): Promise<T> {
    try {
      const res: any = await firstValueFrom(request);
      if (res?.success === false) {
        throw new ApiError(
          `ERROR ${res?.error?.code} : ${res?.error?.message} - ${res?.error?.details}`,
          res?.error?.code
        );
      }

      return res?.data ?? res;
    } catch (err) {
      if (!handleError) {
        throw err;
      }

      //On envoie à Sentry qu'en prod, et on affiche le message dans tous les cas
      if (environment.production) {
        logError(err);
      } else {
        console.error(err);
      }

      if (environment.vendor) {
        this.toastService.showErrorToast('Une erreur est survenue', this.errorToMessage(err));
      }

      throw err;
    }
  }

  protected errorToMessage(err: any): string {
    if (err?.error?.message) {
      return err.error.message;
    }
    return err?.message ?? (typeof err === 'string' ? err : JSON.stringify(err));
  }

  protected async get<T = any>(
    endpoint: string,
    params?: HttpParams,
    responseType?: string,
    handleError: boolean = true
  ): Promise<T> {
    const options = await this.getOptions({ params });
    if (responseType) {
      options.responseType = responseType;
    }
    return this.callApi(this.http.get<T>(endpoint, options), handleError);
  }

  protected async post<T = any>(
    endpoint: string,
    body?: any,
    params?: HttpParams,
    responseType?: string,
    handleError: boolean = true
  ): Promise<T> {
    const options = await this.getOptions({ params });
    if (responseType) {
      options.responseType = responseType;
    }
    return this.callApi(this.http.post<T>(endpoint, body, options), handleError);
  }

  protected async put<T = any>(endpoint: string, body?: any, params?: HttpParams): Promise<T> {
    const options = await this.getOptions({ params });
    return this.callApi(this.http.put<T>(endpoint, body, options));
  }

  protected async delete<T = any>(endpoint: string, body?: any, params?: HttpParams): Promise<any> {
    const options = (await this.getOptions({ params })) as any;
    options.body = body;
    return this.callApi(this.http.delete<T>(endpoint, options));
  }

  // Options de chaque requête
  protected async getOptions({ params, responseType }: { params?: HttpParams; responseType?: any } = {}) {
    const vendorToken = this.current.vendor ? await this.current.vendor.accessToken() : '';
    const headers: any = {
      'X-CustomerData': [
        this.customer.userDatas ? this.sanitizeHeaderValue(this.customer.userDatas.lastname) : '', //Nom du client
        this.customer.userDatas ? this.sanitizeHeaderValue(`${this.customer.userDatas.firstname} ${this.customer.userDatas.lastname}`) : '',
        this.customer.userDatas ? this.customer.userDatas.id : '', //ID du client
      ],
    };

    if (this.current.vendor) {
      if (vendorToken) {
        headers.Authorization = `Bearer ${vendorToken}`;
      }

      headers['X-Vendor-StoreId'] = this.current.vendor.store.SAPStoreId ?? '';
      headers['X-Vendor-LegacyStoreId'] = this.current.vendor.store.legacyStoreId ?? '';
    }

    return {
      withCredentials: true,
      headers: new HttpHeaders(headers),
      responseType: responseType !== undefined ? responseType : null,
      params,
    };
  }

  private sanitizeHeaderValue(value?: string): string {
    return String(value ?? '').replaceAll(',', '').trim();
  }

  protected buildHttpParamsFromObject(input: object): HttpParams {
    input = input ?? {};
    return Object.entries(input).reduce((httpParams, [key, value]) => {
      return value !== undefined ? httpParams.set(key, value) : httpParams;
    }, new HttpParams());
  }

  /*protected async get(relativeEndpoint: string, params?: object): Promise<any> {
    const httpParams = params ? this.buildHttpParamsFromObject(params) : undefined;
    const options = await this.getOptions({ params: httpParams });
    return this.callApi(this.http.get(`${baseApi}${relativeEndpoint}`, options));
  }*/
}
